import React, { useState } from "react";
import styles from "./AppUsagePage.module.scss";

/*
 * Enkelt søylediagram per dag (én serie, ingen bibliotek). Hver søyle har et usynlig, bredere treffområde med
 * verktøytips, og tallene finnes også som tabell for skjermlesere.
 */

export interface DailyBar {
  date: string;
  value: number;
  /** Ekstra linje i verktøytipset, f.eks. «312 brukere». */
  detail?: string;
}

const nf = (n: number) => n.toLocaleString("nb-NO");

const dayLabel = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nb-NO", {
    day: "numeric",
    month: "short",
  });

const DailyBars: React.FC<{
  data: DailyBar[];
  /** Hva verdien er, f.eks. «aktive brukere». */
  unit: string;
  height?: number;
}> = ({ data, unit, height = 160 }) => {
  const [hover, setHover] = useState<number | null>(null);
  if (!data.length) return null;
  const max = Math.max(1, ...data.map((d) => d.value));
  const width = 600;
  const top = 8;
  const bottom = 20;
  const plotH = height - top - bottom;
  const slot = width / data.length;
  const barW = Math.max(2, Math.min(18, slot - 2));
  const ticks = [0, Math.round(max / 2), max];
  const labelEvery = Math.ceil(data.length / 6);
  const h = hover !== null ? data[hover] : null;

  return (
    <div className={styles.chart}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${unit} per dag`}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => {
          const y = top + plotH - (t / max) * plotH;
          return (
            <line key={t} x1={0} x2={width} y1={y} y2={y} className={styles.gridLine} />
          );
        })}
        {data.map((d, i) => {
          const bh = (d.value / max) * plotH;
          const x = i * slot + (slot - barW) / 2;
          const y = top + plotH - bh;
          const r = Math.min(4, barW / 2, bh);
          return (
            <g key={d.date}>
              {bh > 0 && (
                <path
                  className={hover === i ? styles.barOn : styles.bar}
                  d={`M${x},${top + plotH} V${y + r} Q${x},${y} ${x + r},${y} H${x + barW - r} Q${x + barW},${y} ${x + barW},${y + r} V${top + plotH} Z`}
                />
              )}
              <rect
                x={i * slot}
                y={0}
                width={slot}
                height={height}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
              />
              {i % labelEvery === 0 && (
                <text
                  x={i * slot + slot / 2}
                  y={height - 4}
                  textAnchor="middle"
                  className={styles.axis}
                >
                  {dayLabel(d.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className={styles.chartMax}>{nf(max)}</div>
      {h && (
        <div
          className={styles.tip}
          style={{
            left: `${Math.min(85, Math.max(5, ((hover! + 0.5) / data.length) * 100))}%`,
          }}
        >
          <b>{dayLabel(h.date)}</b>
          <span>
            {nf(h.value)} {unit}
          </span>
          {h.detail && <small>{h.detail}</small>}
        </div>
      )}
      <table className={styles.srOnly}>
        <caption>{unit} per dag</caption>
        <tbody>
          {data.map((d) => (
            <tr key={d.date}>
              <th>{d.date}</th>
              <td>{d.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DailyBars;
